import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { motion, AnimatePresence } from 'framer-motion'
import { createPortal } from 'react-dom'
import {
    Share2,
    Copy,
    Check,
    Mail,
    Phone,
    MapPin,
    Globe,
    ExternalLink,
    MessageCircle,
    CreditCard,
    ArrowRight,
    Linkedin,
    Instagram,
    Youtube,
    Twitter,
    Facebook,
    Github,
    Award,
    Briefcase,
    MessageSquare,
    Star,
    Lock,
    X,
    UserPlus
} from 'lucide-react'
import { profileAPI, analyticsAPI } from '../services/api'
import toast from 'react-hot-toast'
import QRCode from 'react-qr-code'
import NotFoundPage from './NotFoundPage'

const PublicProfilePage = () => {
    const { username } = useParams()
    const navigate = useNavigate()
    const [profile, setProfile] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [copiedLink, setCopiedLink] = useState(false)
    const [isLocked, setIsLocked] = useState(false)
    const [showPayModal, setShowPayModal] = useState(false)
    const [payAmount, setPayAmount] = useState('')
    const [showPaymentDetails, setShowPaymentDetails] = useState(false)
    const [isNotFound, setIsNotFound] = useState(false)

    useEffect(() => {
        fetchProfile()
        if (!sessionStorage.getItem('sessionId')) {
            sessionStorage.setItem('sessionId', Date.now().toString() + Math.random().toString(36).substr(2, 9))
        }
    }, [username])

    const fetchProfile = async () => {
        try {
            setIsLoading(true)
            const response = await profileAPI.getPublicProfile(username)
            if (response.data.success && response.data.data) {
                setProfile(response.data.data)
                trackProfileView(response.data.data._id)
            } else {
                setIsNotFound(true)
            }
        } catch (error) {
            console.error('Failed to fetch profile:', error)
            if (error.response?.status === 403 && error.response?.data?.isLocked) {
                setIsLocked(true)
            } else {
                setIsNotFound(true)
            }
        } finally {
            setIsLoading(false)
        }
    }

    const getDeviceType = () => {
        const ua = navigator.userAgent;
        if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return "Tablet";
        if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) return "Mobile";
        return "Desktop";
    };

    const trackProfileView = async (profileId) => {
        try {
            await analyticsAPI.trackEvent({
                eventType: 'profile_view',
                eventAction: 'view',
                profile: profileId,
                metadata: {
                    url: window.location.href,
                    sessionId: sessionStorage.getItem('sessionId'),
                    device: getDeviceType()
                }
            })
        } catch (error) { console.error('Tracking error', error) }
    }

    const trackClick = async (type, action, value) => {
        try {
            await analyticsAPI.trackEvent({
                eventType: type,
                eventAction: action,
                profile: profile._id,
                metadata: { 
                    value, 
                    sessionId: sessionStorage.getItem('sessionId'),
                    device: getDeviceType() 
                }
            })
        } catch (error) { console.error('Tracking error', error) }
    }

    const handleCopyLink = () => {
        navigator.clipboard.writeText(window.location.href)
        setCopiedLink(true)
        toast.success('Link copied to clipboard!')
        setTimeout(() => setCopiedLink(false), 2000)
    }

    const handleSaveContact = () => {
        if (!profile) return;

        let vcard = `BEGIN:VCARD\nVERSION:3.0\n`;
        if (profile.displayName) vcard += `FN:${profile.displayName}\n`;
        if (profile.company) vcard += `ORG:${profile.company}\n`;
        if (profile.jobTitle) vcard += `TITLE:${profile.jobTitle}\n`;
        if (profile.contactInfo?.phone) vcard += `TEL;TYPE=WORK,VOICE:${profile.contactInfo.phone}\n`;
        if (profile.contactInfo?.email) vcard += `EMAIL;TYPE=WORK:${profile.contactInfo.email}\n`;
        vcard += `URL:${window.location.href}\n`;
        
        if (profile.socialLinks) {
            Object.values(profile.socialLinks).forEach(val => {
                if (val && typeof val === 'string' && val.startsWith('http')) {
                    vcard += `URL:${val}\n`;
                }
            });
        }
        
        if (profile.location) vcard += `ADR;TYPE=WORK:;;${profile.location};;;;\n`;
        vcard += `NOTE:Digital Profile via FiindIt\n`;
        vcard += `END:VCARD`;

        const blob = new Blob([vcard], { type: 'text/vcard' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${profile.displayName?.replace(/\\s+/g, '_') || 'contact'}.vcf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success('Saving to Contacts...');
        trackClick('interaction', 'save_contact', 'vcard');
    }

    if (isLoading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
        )
    }

    if (isNotFound) {
        return <NotFoundPage />
    }

    if (isLocked) {
        return (
            <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-6 text-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-gray-800 p-8 rounded-3xl max-w-md w-full border border-gray-700 shadow-2xl"
                >
                    <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Lock className="w-10 h-10 text-gray-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-4">Profile Unavailable</h1>
                    <p className="text-gray-400 mb-8">
                        This profile is currently inactive. If you are the owner, please complete your purchase to activate your digital usage.
                    </p>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate('/products')}
                        className="w-full bg-green-500 text-white py-4 rounded-xl font-bold text-lg shadow-lg shadow-primary-500/20"
                    >
                        Activate Profile
                    </motion.button>
                </motion.div>
            </div>
        )
    }

    if (!profile) return null

    // ------------------------------------------------------------------
    // Premium Design Variants
    // ------------------------------------------------------------------
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1, delayChildren: 0.1 }
        }
    }

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: "spring", stiffness: 80, damping: 15 }
        }
    }

    // Dynamic Colors
    const bgColor = profile.colors?.background || '#000000'
    const cardColor = profile.colors?.card || '#111111'
    const textColor = profile.colors?.text || '#ffffff'

    // ------------------------------------------------------------------
    // Payment Logic
    // ------------------------------------------------------------------

    const handlePayClick = () => {
        const upiId = profile.paymentInfo?.upiId
        if (!upiId || !upiId.includes('@')) {
            toast.error('🚫 Merchant UPI ID is invalid. Please contact the profile owner.')
            return
        }
        setShowPayModal(true)
        setPayAmount('')
        setShowPaymentDetails(false)
    }

    const handleCopyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text)
        toast.success(`${label} copied!`)
    }

    const processPayment = () => {
        if (!payAmount || isNaN(payAmount) || parseFloat(payAmount) <= 0) {
            toast.error('Please enter a valid amount greater than 0');
            return;
        }
        setShowPaymentDetails(true)
    }

    const handleUpiPayment = (appType) => {
        const upiId = profile.paymentInfo.upiId.trim()
        // Removed amount validation here as it's handled in processPayment 
        // and causing security issues when passed in deep link

        if (!upiId) {
            toast.error("UPI ID not available")
            return
        }

        // We intentionally do NOT include 'am' (amount) parameter
        // to avoid "Payment declined for security reasons" in some UPI apps
        // for personal UPI IDs.
        const params = new URLSearchParams({
            pa: upiId,
            cu: "INR",
            tn: "Profile Payment"
        })

        const upiLink = `upi://pay?${params.toString()}`

        // Track and Redirect
        trackClick('payment_click', appType, upiId)
        window.location.href = upiLink
    }


    // ------------------------------------------------------------------
    // SEO Meta Data
    // ------------------------------------------------------------------
    const locationPart = profile.location ? ` | ${profile.location}` : ''
    const keywordsList = [...(profile.categories || []), ...(profile.subcategories || [])].filter(Boolean)
    const categoryPart = keywordsList.length > 0 ? ` | ${keywordsList.join(', ')}` : ''
    const seoTitle = `${profile.displayName} | FiindIt${locationPart}${categoryPart}`
    const defaultDescription = `${profile.displayName} is a professional ${profile.categories?.[0] || 'service provider'}${profile.location ? ` based in ${profile.location}` : ''}.`
    const seoDescription = profile.bio ? `${profile.bio}` : defaultDescription
    const seoKeywords = [profile.displayName, profile.company, profile.location, ...keywordsList].filter(Boolean).join(', ')

    return (
        <div
            className="min-h-screen flex flex-col md:items-center md:justify-center overflow-x-hidden md:p-4"
            style={{ backgroundColor: bgColor, color: textColor }}
        >
            <Helmet>
                <title>{seoTitle}</title>
                <meta name="description" content={seoDescription} />
                <meta name="keywords" content={seoKeywords} />
                <link rel="canonical" href={`https://www.fiindit.in/visit/p/${username}`} />
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://www.fiindit.in/visit/p/${username}`} />
                <meta property="og:title" content={seoTitle} />
                <meta property="og:description" content={seoDescription} />
                <meta property="og:image" content={profile.avatar || 'https://fiindit.in/assets/default-avatar.png'} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content={`https://www.fiindit.in/visit/p/${username}`} />
                <meta name="twitter:title" content={seoTitle} />
                <meta name="twitter:description" content={seoDescription} />
                <meta name="twitter:image" content={profile.avatar || 'https://fiindit.in/assets/default-avatar.png'} />

                {/* Schema.org Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "LocalBusiness",
                        "name": profile.displayName,
                        "url": `https://www.fiindit.in/visit/p/${username}`,
                        "image": profile.avatar,
                        "description": seoDescription,
                        "address": profile.location,
                        "knowsAbout": keywordsList,
                        "sameAs": [
                            profile.socialLinks?.website,
                            profile.socialLinks?.linkedin,
                            profile.socialLinks?.twitter,
                            profile.socialLinks?.facebook,
                            profile.socialLinks?.instagram,
                            profile.socialLinks?.youtube
                        ].filter(Boolean)
                    })}
                </script>
            </Helmet>

            {/* Main Card */}
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="relative z-10 w-full md:max-w-sm min-h-screen md:min-h-fit md:rounded-[32px] overflow-hidden shadow-none md:shadow-2xl flex flex-col justify-between"
                style={{ backgroundColor: cardColor }}
            >

                {/* 3D Background Name (Fixed to be MASSIVE behind content) */}
                <div className="absolute inset-0 flex items-center justify-center select-none pointer-events-none opacity-25 overflow-hidden">
                    {/* Using very large size and stroke for contrast */}
                    <div className="text-[110vh] md:text-[50vw] font-black uppercase text-transparent bg-clip-text bg-gradient-to-br from-white/10 to-transparent whitespace-nowrap leading-none"
                        style={{
                            WebkitTextStroke: '4px rgba(255,255,255,0.1)',
                            transform: 'rotate(0deg)',
                            filter: 'drop-shadow(0 0 50px rgba(0,0,0,0.5))'
                        }}>
                        {profile.displayName?.charAt(0)}
                    </div>
                </div>

                {/* Top Navbar Actions */}
                <div className="absolute top-4 right-4 z-50 flex space-x-2">
                   
                    <button
                        onClick={() => {
                            if (navigator.share) navigator.share({ title: profile.displayName, url: window.location.href }).catch(() => { })
                            else handleCopyLink()
                        }}
                        className="p-2 bg-black/20 backdrop-blur-md border border-white/10 rounded-full hover:bg-white/10 transition-all active:scale-95"
                    >
                        <Share2 className="w-4 h-4 text-white/70" />
                    </button>
                    <button
                        onClick={handleSaveContact}
                        title="Save to Contacts"
                        className="p-2 bg-black/20 backdrop-blur-md border border-white/10 rounded-full hover:bg-white/10 transition-all active:scale-95"
                    >
                        <UserPlus className="w-4 h-4 text-white/70" />
                    </button>
                </div>

                {/* ---------------------------
            HEADER SECTION
           --------------------------- */}
                <div className="relative w-full pt-10 pb-2 flex flex-col items-center z-10 shrink-0">

                    {/* Header 3D Name (Smaller Context) */}
                    <div className="w-full h-24 flex items-center justify-center relative overflow-hidden mb-[-10px]">
                        <div className="text-5xl md:text-6xl font-black uppercase tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white/20 to-white/5"
                            style={{
                                transform: 'perspective(500px) rotateX(20deg)',
                                textShadow: '0 10px 20px rgba(0,0,0,0.5)'
                            }}>
                            {profile.displayName?.split(' ')[0]}
                        </div>
                    </div>

                    {/* Profile Avatar */}
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", duration: 0.6 }}
                        className="relative z-10"
                    >
                        <div className="w-28 h-28 rounded-full p-1" style={{ backgroundColor: cardColor }}>
                            <div className="w-full h-full rounded-full overflow-hidden bg-gray-800 border border-white/10 shadow-xl">
                                {profile.avatar ? (
                                    <img src={profile.avatar} alt={profile.displayName} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-800 text-2xl font-bold">
                                        {profile.displayName?.charAt(0)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>

                    {/* Name & Title */}
                    <div className="text-center mt-3 px-4">
                        <h1 className="text-2xl font-bold text-white mb-1 tracking-tight">{profile.displayName}</h1>

                    </div>
                </div>

                {/* ---------------------------
            CONTENT AREA (Flex-grow to fill space)
           --------------------------- */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="px-6 pb-6 pt-2 flex-grow flex flex-col justify-evenly space-y-4 z-10"
                >

                    {/* 1. Google Review (Google Logo + Stars Style), placed ABOVE Payment */}
                    {(profile.socialLinks?.googleReview || profile.customLinks?.find(l => l.title?.toLowerCase().includes('review'))) && (
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                                trackClick('internal_nav', 'ai_review_generator', `/review/${username}`)
                                navigate(`/review/${username}`)
                            }}
                            className="w-full bg-[#181818] border border-white/10 rounded-2xl py-3 px-6 flex items-center justify-between shadow-lg"
                        >
                            {/* Google Logo Recreation */}
                            <div className="flex items-center gap-1 font-product-sans font-bold text-2xl tracking-tight">
                                <span className="text-blue-500">G</span>
                                <span className="text-red-500">o</span>
                                <span className="text-yellow-500">o</span>
                                <span className="text-blue-500">g</span>
                                <span className="text-green-500">l</span>
                                <span className="text-red-500">e</span>
                            </div>

                            {/* 5 Stars */}
                            <div className="flex gap-0.5">
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <Star key={s} className="w-6 h-6 text-yellow-500 fill-yellow-500" />
                                ))}
                            </div>
                        </motion.button>
                    )}


                    {/* 2. Payment Button */}
                



                    {/* 3. Contact Icons: Email, Phone (Moved Down) */}
                    <motion.div variants={itemVariants} className="flex justify-center gap-6">
                        {profile.contactInfo?.email && (
                            <CompactActionBtn icon={<Mail className="w-6 h-6" />} onClick={() => window.location.href = `mailto:${profile.contactInfo.email}`} />
                        )}
                        {profile.contactInfo?.phone && (
                            <CompactActionBtn icon={<Phone className="w-6 h-6" />} onClick={() => window.location.href = `tel:${profile.contactInfo.phone}`} />
                        )}
                    </motion.div>


                    {/* 4. Social Links (3 Column Grid) */}
                    {(profile.socialLinks || (profile.customLinks && profile.customLinks.length > 0)) && (
                        <motion.div variants={itemVariants} className="w-full">
                            <div className="grid grid-cols-3 gap-5">
                                {/* Map social links except Google Review */}
                                {Object.entries(profile.socialLinks || {}).map(([key, value]) => {
                                    if (!value || key === 'googleReview') return null
                                    return (
                                        <SocialIconBtn
                                            key={key}
                                            type={key}
                                            onClick={() => {
                                                trackClick('social_click', key, value)
                                                window.open(value, '_blank')
                                            }}
                                        />
                                    )
                                })}
                                {profile.customLinks?.map((link, i) => {
                                    if (link.title?.toLowerCase().includes('review')) return null
                                    return (
                                        <button
                                            key={i}
                                            onClick={() => window.open(link.url, '_blank')}
                                            className="aspect-square w-full rounded-2xl mx-auto bg-white/5 border border-white/5 flex items-center justify-center text-4xl hover:bg-white/10 transition-colors"
                                        >
                                            {link.emoji || '🔗'}
                                        </button>
                                    )
                                })}
                            </div>
                        </motion.div>
                    )}

                    {/* Related Profiles (Internal Linking for SEO) */}
                  

                    {/* Footer */}
                    <div className="mt-auto pt-2 text-center opacity-30 text-[20px]  tracking-widest text-white shrink-0">
                        ©     FiindIt
                    </div>

                </motion.div>
            </motion.div>

            {/* Payment Modal */}
            <AnimatePresence>
                {showPayModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
                        onClick={() => setShowPayModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-[#181818] w-full max-w-sm rounded-[32px] p-6 border border-white/10 shadow-2xl"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    <CreditCard className="w-5 h-5 text-orange-500" />
                                    {showPaymentDetails ? 'Payment Details' : 'Enter Amount'}
                                </h3>
                                <button
                                    onClick={() => setShowPayModal(false)}
                                    className="p-2 bg-white/5 rounded-full hover:bg-white/10 transition-colors"
                                >
                                    <X className="w-5 h-5 text-gray-400" />
                                </button>
                            </div>

                            {!showPaymentDetails ? (
                                <div className="space-y-6">
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-400">₹</span>
                                        <input
                                            type="number"
                                            value={payAmount}
                                            onChange={(e) => setPayAmount(e.target.value)}
                                            placeholder="0"
                                            className="w-full bg-black/30 border border-white/10 rounded-2xl py-8 pl-12 pr-4 text-4xl font-bold text-white placeholder-gray-600 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all text-center"
                                            autoFocus
                                        />
                                    </div>

                                    <button
                                        onClick={processPayment}
                                        className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl text-white font-bold text-lg shadow-lg shadow-orange-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
                                    >
                                        Continue
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-400 text-center mb-2">
                                        Choose your payment app
                                    </p>
                                    <div className="text-center mb-4">
                                        <span className="text-3xl font-bold text-white">₹{parseFloat(payAmount).toFixed(2)}</span>
                                    </div>

                                    {/* PhonePe Button */}
                                    <button
                                        onClick={() => handleUpiPayment('phonepe')}
                                        className="w-full py-4 px-4 bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl text-white font-bold text-lg shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3"
                                    >
                                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-purple-600 font-black text-sm">
                                            Pe
                                        </div>
                                        <span>Pay with PhonePe</span>
                                    </button>

                                    {/* Google Pay Button */}
                                    <button
                                        onClick={() => handleUpiPayment('gpay')}
                                        className="w-full py-4 px-4 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl text-white font-bold text-lg shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3"
                                    >
                                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-blue-600 font-black text-sm">
                                            G
                                        </div>
                                        <span>Pay with Google Pay</span>
                                    </button>

                                    {/* Paytm Button */}
                                    <button
                                        onClick={() => handleUpiPayment('paytm')}
                                        className="w-full py-4 px-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl text-white font-bold text-lg shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3"
                                    >
                                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-blue-600 font-black text-sm">
                                            P
                                        </div>
                                        <span>Pay with Paytm</span>
                                    </button>

                                    {/* Generic UPI Button */}
                                    <button
                                        onClick={() => handleUpiPayment('upi')}
                                        className="w-full py-3 px-4 bg-white/10 border border-white/20 rounded-xl text-white font-semibold text-base hover:bg-white/20 transition-all flex items-center justify-center gap-2"
                                    >
                                        <CreditCard className="w-5 h-5" />
                                        <span>Other UPI Apps</span>
                                    </button>

                                    {/* Show UPI ID for manual entry if needed */}
                                    <div className="pt-4 border-t border-white/10">
                                        <p className="text-xs text-gray-500 text-center mb-2">Or pay manually using UPI ID:</p>
                                        <div className="flex items-center justify-between bg-black/30 border border-white/10 rounded-lg p-3">
                                            <span className="text-white font-mono text-xs">{profile.paymentInfo.upiId}</span>
                                            <button
                                                onClick={() => handleCopyToClipboard(profile.paymentInfo.upiId, 'UPI ID')}
                                                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                                            >
                                                <Copy className="w-4 h-4 text-orange-500" />
                                            </button>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => setShowPaymentDetails(false)}
                                        className="w-full py-2 text-sm text-gray-500 hover:text-gray-300 transition-colors"
                                    >
                                        Change Amount
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
// ------------------------------------------------------------------
// Sub-Components
// ------------------------------------------------------------------

const CompactActionBtn = ({ icon, onClick, highlight }) => (
    <button
        onClick={onClick}
        className={`
            w-16 h-16 rounded-2xl flex items-center justify-center transition-all active:scale-95
            ${highlight
                ? 'bg-gradient-to-br from-pink-500/20 to-rose-600/20 border border-pink-500/30 shadow-[0_0_15px_rgba(236,72,153,0.3)]'
                : 'bg-white/5 border border-white/10 hover:bg-white/10'
            }
        `}
    >
        <div className={highlight ? 'animate-pulse' : ''}>
            {icon}
        </div>
    </button>
)

const SocialIconBtn = ({ type, onClick }) => {
    const info = getSocialInfo(type)
    return (
        <motion.button
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClick}
            className="aspect-square w-full rounded-3xl flex items-center justify-center bg-[#181818] border border-white/5 shadow-lg group relative overflow-hidden"
        >
            <div className={`absolute inset-0 bg-gradient-to-br ${info.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
            <div className="relative z-10 text-white/50 group-hover:text-white transition-colors">
                {info.icon}
            </div>
        </motion.button>
    )
}

// Helper for Icons & Colors
const getSocialInfo = (type) => {
    const defaults = {
        icon: <Globe className="w-8 h-8" />,
        gradient: 'from-gray-600 to-gray-800'
    }

    const map = {
        whatsapp: {
            icon: <MessageCircle className="w-8 h-8" />,
            gradient: 'from-green-500 to-emerald-600'
        },
        linkedin: {
            icon: <Linkedin className="w-8 h-8" />,
            gradient: 'from-blue-600 to-blue-800'
        },
        instagram: {
            icon: <Instagram className="w-8 h-8" />,
            gradient: 'from-purple-500 to-pink-600'
        },
        youtube: {
            icon: <Youtube className="w-8 h-8" />,
            gradient: 'from-red-600 to-red-700'
        },
        twitter: {
            icon: <Twitter className="w-8 h-8" />,
            gradient: 'from-sky-400 to-blue-500'
        },
        facebook: {
            icon: <Facebook className="w-8 h-8" />,
            gradient: 'from-blue-600 to-blue-800'
        },
        github: {
            icon: <Github className="w-8 h-8" />,
            gradient: 'from-gray-700 to-gray-900'
        },
        googleMap: {
            icon: <MapPin className="w-8 h-8" />,
            gradient: 'from-blue-500 to-cyan-500'
        },
        googleReview: {
            icon: <MessageSquare className="w-8 h-8" />,
            gradient: 'from-pink-500 to-rose-500'
        }
    }

    return map[type] || defaults
}

export default PublicProfilePage
